<template>
  <div class="sbs-page">
    <div class="sbs-hero">
      <div class="sbs-hero__text">
        <div class="sbs-hero__badge">Admin panel</div>
        <h2 class="sbs-hero__title">Subsidiyalar</h2>
        <p class="sbs-hero__sub">
          Har bir yil o'z jadvaliga ega: yilga ustunlar biriktiriladi, qiymatlar esa
          choraklar bo'yicha kiritiladi
        </p>
      </div>
    </div>

    <div class="sbs-yearbar">
      <div class="sbs-yearbar__main">
        <span class="sbs-yearbar__label">Jadval yili</span>
        <el-select v-model="year" :loading="loadingYears" style="width: 130px" @change="onYearSelect">
          <el-option v-for="y in years" :key="y" :label="y" :value="y">
            <span class="sbs-year-option">
              <span>{{ y }}</span>
              <span v-if="yearHasValues(y)" class="sbs-year-dot" title="Qiymatlar kiritilgan" />
            </span>
          </el-option>
        </el-select>
        <span class="sbs-yearbar__meta">{{ yearMeta }}</span>
      </div>
      <el-button @click="newYearVisible = true">
        <span class="sbs-btn-plus">+</span> Yangi yil
      </el-button>
    </div>

    <el-tabs v-model="activeTab" class="sbs-tabs">
      <el-tab-pane name="matrix">
        <template #label>
          <span class="sbs-tab-label">
            <svg class="sbs-tab-ico" viewBox="0 0 16 16" width="15" height="15" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
              <rect x="1.5" y="2.5" width="13" height="11" rx="1.5"/>
              <path d="M1.5 6.5h13M5.5 6.5v7"/>
            </svg>
            Jadval
          </span>
        </template>
        <SubsidiesMatrix
            v-if="activeTab === 'matrix' && year"
            ref="matrix"
            :year="year"
            :periods="periods"
            @go-tab="activeTab = $event"
            @saved="reloadContext"
        />
      </el-tab-pane>

      <el-tab-pane name="rows">
        <template #label>
          <span class="sbs-tab-label">
            <svg class="sbs-tab-ico" viewBox="0 0 16 16" width="15" height="15" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
              <path d="M2 3h5M2 8h9M2 13h7"/>
            </svg>
            Ko'rsatkichlar
          </span>
        </template>
        <SubsidiesRows v-if="activeTab === 'rows'" :year="year"/>
      </el-tab-pane>

      <el-tab-pane name="columns">
        <template #label>
          <span class="sbs-tab-label">
            <svg class="sbs-tab-ico" viewBox="0 0 16 16" width="15" height="15" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
              <rect x="1.5" y="2.5" width="13" height="11" rx="1.5"/>
              <path d="M5.5 2.5v11M10.5 2.5v11"/>
            </svg>
            Ustunlar
            <span v-if="year" class="sbs-tab-count">{{ yearColumnCount }}</span>
          </span>
        </template>
        <SubsidiesColumns v-if="activeTab === 'columns' && year" :year="year" @changed="reloadContext"/>
      </el-tab-pane>
    </el-tabs>

    <SubsidiesNewYearDialog v-model="newYearVisible" :years="years" :columns="allColumns"
      @created="onYearCreated"/>
  </div>
</template>

<script>
import SubsidiesMatrix from "./SubsidiesMatrix.vue";
import SubsidiesRows from "./SubsidiesRows.vue";
import SubsidiesColumns from "./SubsidiesColumns.vue";
import SubsidiesNewYearDialog from "./SubsidiesNewYearDialog.vue";
import {API_BASE, mergeYears, normalizePeriods, quarterLabel, yearsFromColumns} from "./subsidiesTypes";

export default {
  name: "SubsidiesIndex",
  components: {SubsidiesMatrix, SubsidiesRows, SubsidiesColumns, SubsidiesNewYearDialog},
  data() {
    return {
      activeTab: "matrix",
      year: null,
      // Tanlangan yil bekor qilinganda qaytariladigan qiymat.
      appliedYear: null,
      periods: [],
      allColumns: [],
      // Yangi ochilgan, lekin hali ustuni/qiymati yo'q yillar — server ularni bilmaydi.
      draftYears: [],
      loadingYears: false,
      newYearVisible: false,
    };
  },
  computed: {
    years() {
      return mergeYears(
          this.periods.map((p) => p.year),
          yearsFromColumns(this.allColumns),
          this.draftYears,
          // Bo'sh bazada ham tanlangan yil ro'yxatda ko'rinib tursin.
          [this.year],
      );
    },
    yearColumnCount() {
      return this.allColumns.filter((c) => c.year === null || c.year === undefined || Number(c.year) === this.year).length;
    },
    yearMeta() {
      if (!this.year) return "";
      const filled = this.periods.find((p) => p.year === this.year)?.quarters || [];
      const parts = [`${this.yearColumnCount} ta ustun`];
      parts.push(filled.length ? `to'ldirilgan: ${filled.map(quarterLabel).join(", ")}` : "choraklar bo'sh");
      return parts.join(" · ");
    },
  },
  created() {
    if (["matrix", "rows", "columns"].includes(this.$route.query.tab)) {
      this.activeTab = this.$route.query.tab;
    }
    this.loadContext();
  },
  methods: {
    yearHasValues(year) {
      const period = this.periods.find((p) => p.year === Number(year));
      return !!(period && (period.quarters.length || period.has_annual));
    },
    // Yil ro'yxati ikkita manbadan yig'iladi: qiymati bor yillar (`/admin/periods`)
    // va ustun biriktirilgan yillar (`column.year`). Ikkinchisi bo'lmasa, hali
    // to'ldirilmagan yangi yil ro'yxatdan yo'qolib qolardi.
    loadContext(keepYear = false) {
      this.loadingYears = true;
      Promise.all([
        this.$http.get(`${API_BASE}/admin/periods`).then(({data}) => normalizePeriods(data)).catch(() => []),
        this.$http.get(`${API_BASE}/columns`, {params: {include_inactive: true}})
            .then(({data}) => (Array.isArray(data) ? data : [])).catch(() => []),
      ])
          .then(([periods, columns]) => {
            this.periods = periods;
            this.allColumns = columns;
            if (!keepYear || !this.years.includes(this.year)) {
              this.year = this.years[0] || new Date().getFullYear();
              this.appliedYear = this.year;
            }
          })
          .finally(() => {
            this.loadingYears = false;
          });
    },
    reloadContext() {
      this.loadContext(true);
    },
    // Jadval tabida saqlanmagan katak bo'lsa, yilni almashtirishdan oldin so'raladi.
    onYearSelect(next) {
      const grid = this.$refs.matrix;
      if (!grid?.hasUnsavedChanges?.()) {
        this.applyYear(next);
        return;
      }
      this.year = this.appliedYear;
      grid.confirmLeavePeriod(next, null)
          .then(() => this.applyYear(next))
          .catch(() => {
          });
    },
    applyYear(next) {
      this.year = next;
      this.appliedYear = next;
    },
    onYearCreated({year, copied}) {
      if (!this.draftYears.includes(year)) this.draftYears.push(year);
      this.applyYear(year);
      this.activeTab = copied ? "matrix" : "columns";
      this.reloadContext();
    },
  },
};
</script>

<style scoped lang="scss">
$brand: #007791;
$navy: #0a194e;

.sbs-page {
  padding-bottom: 8px;
}

.sbs-hero {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
  flex-wrap: wrap;
  padding: 22px 26px;
  margin-bottom: 16px;
  border-radius: 16px;
  color: #fff;
  background:
      radial-gradient(120% 140% at 100% 0%, rgba(255, 255, 255, 0.18), transparent 55%),
      linear-gradient(135deg, #007791 0%, #0a5f77 55%, #0a194e 130%);
  box-shadow: 0 12px 30px rgba(0, 92, 114, 0.25);

  &__badge {
    display: inline-block;
    padding: 3px 10px;
    margin-bottom: 8px;
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.4px;
    text-transform: uppercase;
    background: rgba(255, 255, 255, 0.16);
    border-radius: 20px;
  }

  &__title {
    margin: 0;
    font-family: "Montserrat-Bold", sans-serif;
    font-size: 24px;
  }

  &__sub {
    margin: 6px 0 0;
    font-size: 13px;
    max-width: 560px;
    opacity: 0.85;
  }
}

.sbs-yearbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 14px;
  margin-bottom: 18px;
  padding: 12px 18px;
  border: 1px solid #e3e9f0;
  border-radius: 12px;
  background: #fbfcfe;

  &__main {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 12px;
  }

  &__label {
    font-size: 12px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.4px;
    color: #8a94a6;
  }

  &__meta {
    font-size: 12.5px;
    color: #8a94a6;
  }
}

.sbs-year-option {
  display: inline-flex;
  align-items: center;
  gap: 7px;
}

.sbs-year-dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: $brand;
}

.sbs-btn-plus {
  font-size: 16px;
  margin-right: 2px;
}

.sbs-tabs {
  :deep(.el-tabs__header) {
    margin-bottom: 18px;
  }

  :deep(.el-tabs__item) {
    font-family: "Montserrat-Medium", sans-serif;
    font-size: 14px;
    height: 44px;
  }

  :deep(.el-tabs__item.is-active) {
    color: $brand;
  }

  :deep(.el-tabs__active-bar) {
    background-color: $brand;
    height: 3px;
    border-radius: 3px;
  }
}

.sbs-tab-label {
  display: inline-flex;
  align-items: center;
  gap: 7px;
}

.sbs-tab-count {
  min-width: 18px;
  padding: 0 5px;
  border-radius: 9px;
  background: #eef1f6;
  color: #64748b;
  font-size: 11px;
  line-height: 18px;
  text-align: center;
}

.sbs-tab-ico {
  display: inline-flex;
  flex-shrink: 0;
  opacity: 0.85;
}
</style>
